// Events & Calendar (Phase 3 Module 4) — admin + parent + teacher helpers.

import { supabase, isSupabaseConfigured } from "./supabase";

function assertLive() {
  if (!isSupabaseConfigured) throw new Error("Events module requires live Supabase.");
}

const EVENT_COLS =
  "id, organization_id, classroom_id, title, description, location, starts_at, ends_at, visibility, rsvp_enabled, rsvp_deadline, max_capacity, cover_image_url, cancelled_at, created_by, created_at, updated_at";

// ---------------------------------------------------------------------------
// Listings
// ---------------------------------------------------------------------------

/**
 * List upcoming + recent past events the caller is allowed to see.
 * RLS handles the org / classroom / role filtering.
 */
export async function listEvents({ upcomingOnly = false, limit = 50 } = {}) {
  assertLive();
  let q = supabase
    .from("events")
    .select(EVENT_COLS)
    .is("cancelled_at", null)
    .order("starts_at", { ascending: true })
    .limit(limit);
  if (upcomingOnly) {
    q = q.gte("starts_at", new Date().toISOString());
  }
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getEvent(id) {
  assertLive();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_COLS)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Admin CRUD
// ---------------------------------------------------------------------------

export async function createEvent(payload) {
  assertLive();
  const { data, error } = await supabase
    .from("events")
    .insert(payload)
    .select(EVENT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateEvent(id, patch) {
  assertLive();
  const { data, error } = await supabase
    .from("events")
    .update(patch)
    .eq("id", id)
    .select(EVENT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function cancelEvent(id) {
  assertLive();
  const { data, error } = await supabase
    .from("events")
    .update({ cancelled_at: new Date().toISOString() })
    .eq("id", id)
    .select(EVENT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function uncancelEvent(id) {
  assertLive();
  const { data, error } = await supabase
    .from("events")
    .update({ cancelled_at: null })
    .eq("id", id)
    .select(EVENT_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEvent(id) {
  assertLive();
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// RSVPs
// ---------------------------------------------------------------------------

const RSVP_COLS =
  "id, event_id, family_id, profile_id, status, attendee_count, note, responded_at, updated_at";

/**
 * Fetch the caller's family's RSVP for a given event (null if none yet).
 */
export async function getMyRsvp(eventId) {
  assertLive();
  const { data, error } = await supabase
    .from("event_rsvps")
    .select(RSVP_COLS)
    .eq("event_id", eventId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Batch-fetch a map of {eventId: rsvp|null} for the caller's family.
 */
export async function listMyRsvps(eventIds) {
  assertLive();
  if (!eventIds || eventIds.length === 0) return {};
  const { data, error } = await supabase
    .from("event_rsvps")
    .select(RSVP_COLS)
    .in("event_id", eventIds);
  if (error) throw error;
  const m = {};
  for (const r of data || []) m[r.event_id] = r;
  return m;
}

export async function submitRsvp({ eventId, status, attendeeCount = 1, note = null }) {
  assertLive();
  // Snapshot previous status before mutation so we can decide whether to
  // fire the confirmation email (only on first transition to 'going').
  let previousStatus = null;
  try {
    const prior = await getMyRsvp(eventId);
    previousStatus = prior?.status || null;
  } catch {
    /* non-blocking */
  }

  const { data, error } = await supabase.rpc("rpc_rsvp_event", {
    p_event_id: eventId,
    p_status: status,
    p_attendee_count: attendeeCount,
    p_note: note,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;

  // Fire-and-log confirmation email on the first transition to 'going'.
  // The backend idempotency key (`event_rsvp_confirmed:{event_id}:{profile_id}`)
  // already prevents duplicates, so we can be aggressive here — the worst
  // case is one "already_sent" log row.
  if (status === "going" && previousStatus !== "going") {
    try {
      const { notifyEventRsvpConfirmed } = await import("./notifications");
      const ev = await getEvent(eventId);
      const { data: prof } = await supabase
        .from("profiles")
        .select("id, email, organization_id, organizations:organizations!inner(display_name)")
        .eq("id", row?.profile_id)
        .maybeSingle();
      if (ev && prof?.email) {
        notifyEventRsvpConfirmed({
          event_id: eventId,
          event_title: ev.title,
          event_starts_at: ev.starts_at,
          event_ends_at: ev.ends_at,
          event_location: ev.location,
          attendee_count: row?.attendee_count || attendeeCount,
          parent_email: prof.email,
          parent_profile_id: prof.id,
          organization_id: ev.organization_id,
          organization_name: prof.organizations?.display_name,
        });
      }
    } catch {
      /* fire-and-forget; never block the RSVP on a notification failure */
    }
  }

  return row;
}

export async function listRsvpsForEvent(eventId) {
  assertLive();
  const { data, error } = await supabase
    .from("event_rsvps")
    .select(
      `${RSVP_COLS}, families:families!inner(id, display_name), profiles:profiles!inner(id, full_name, email)`,
    )
    .eq("event_id", eventId)
    .order("responded_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getRsvpCounts(eventId) {
  assertLive();
  const { data, error } = await supabase.rpc("rpc_event_rsvp_counts", {
    p_event_id: eventId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { going: 0, goingAttendees: 0, maybe: 0, notGoing: 0 };
  return {
    going: row.out_going_families ?? 0,
    goingAttendees: row.out_going_attendees ?? 0,
    maybe: row.out_maybe_families ?? 0,
    notGoing: row.out_not_going_families ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const RSVP_STATUS_META = {
  going: { label: "Going", tone: "border-primary/40 bg-primary/10 text-primary-hover" },
  maybe: { label: "Maybe", tone: "border-secondary/50 bg-secondary/20 text-neutral-900" },
  not_going: { label: "Not going", tone: "border-neutral-200 bg-neutral-100 text-neutral-600" },
};

export function formatEventWhen(iso, endIso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dateStr = d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    const timeStr = d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    if (!endIso) return `${dateStr} · ${timeStr}`;
    const end = new Date(endIso);
    const sameDay = d.toDateString() === end.toDateString();
    const endTime = end.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return sameDay
      ? `${dateStr} · ${timeStr} – ${endTime}`
      : `${dateStr} · ${timeStr} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${endTime}`;
  } catch {
    return iso;
  }
}

export function isRsvpOpen(event) {
  if (!event) return false;
  if (event.cancelled_at) return false;
  if (!event.rsvp_enabled) return false;
  if (event.rsvp_deadline && new Date() > new Date(event.rsvp_deadline)) return false;
  return true;
}
